/**
 * Who the signed-in user is, for UI gating.
 *
 * ⚠️ THIS MIRRORS THE API, IT DOES NOT ENFORCE ANYTHING. Every rule here has a
 * server-side counterpart in `apps/api/src/common/project-access.ts`; hiding a
 * tile is presentation, and anything that actually must not reach someone has to
 * be withheld by the API as well. Contract value is the worked example — see
 * `apps/api/src/projects/lib/contract-visibility.ts`.
 *
 * It exists because this predicate had been copy-pasted into App.tsx,
 * ProjectWorkspace.tsx and (nearly) a third and fourth caller. Four copies of a
 * gating rule is three chances for one of them to drift.
 */

/** The shape `usersApi.me()` returns, narrowed to what gating needs. */
export interface Viewer {
  roles: string[];
  permissions: string[];
  canViewAll: boolean;
}

/**
 * A genuine office user: a broad editor, an exec, or an Admin.
 *
 * ⚠️ `reports.view` is deliberately NOT counted, matching `isFieldLeaderScoped`
 * on the API — the FieldLeader role can carry that grant incidentally, and a
 * field leader stays scoped regardless of it.
 */
export const isOfficeUser = (me: Viewer | undefined | null): boolean =>
  (me?.roles.includes('Admin') ?? false) ||
  (me?.permissions.includes('projects.edit') ?? false) ||
  (me?.canViewAll ?? false);

/**
 * Holds the FieldLeader role and none of the office grants.
 *
 * Someone who leads crews AND holds `projects.edit` is an office user who
 * happens to work in the field — they keep full access.
 */
export const isFieldLeaderOnly = (me: Viewer | undefined | null): boolean =>
  (me?.roles.includes('FieldLeader') ?? false) && !isOfficeUser(me);
